// Libraries
import {
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Center,
  AccordionItem as ChakraAccordionItem,
  Text,
} from '@chakra-ui/react';
// Types
import { AccordionItemProps } from './AccordionItem.types';

/**
 * `AccordionItem` component displays a collapsible item with a title and content.
 * When the item is expanded, its background color, border, and title color change.
 * The component utilizes Chakra UI's Accordion components internally.
 *
 * @param {AccordionItemProps} props - The props to configure the AccordionItem component.
 * @param {string} props.title - The title displayed on the accordion item.
 * @param {number} props.index - A unique identifier for the accordion item. It is usually used to display an index or a number.
 * @param {ReactNode} props.children - The content that is displayed when the accordion item is expanded.
 *
 * @notes
 * Please check the storybook example to know how to implement this custom element
 *
 * @example
 * <AccordionItem title="Section 1" index={1}>
 *   This is the content of section 1.
 * </AccordionItem>
 *
 * @returns {ReactElement} The rendered AccordionItem component.
 */
export const AccordionItem = ({ title, index, children, innerChakraProps = {} }: AccordionItemProps) => {
  return (
    <ChakraAccordionItem>
      {({ isExpanded }) => {
        const backgroundColor = isExpanded ? 'base' : 'transparent';
        const color = isExpanded ? 'white' : 'grey.100';
        const border = isExpanded ? 'none' : 'greyDark';
        const transform = isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)';
        return (
          <>
            <AccordionButton py={2} px={4} gap={3} h={8.5}>
              <Center
                boxSize={4}
                borderRadius='base'
                backgroundColor={backgroundColor}
                color={color}
                fontSize='s'
                fontWeight='semibold'
                border={border}
              >
                {index}
              </Center>
              <Text as='h2' flex='1' display='flex'>
                <Text as='span' flex='1' textAlign='left' fontSize='s' fontWeight='medium'>
                  {title}
                </Text>
                <AccordionIcon boxSize={4} transform={transform} />
              </Text>
            </AccordionButton>
            <AccordionPanel {...innerChakraProps}>{children}</AccordionPanel>
          </>
        );
      }}
    </ChakraAccordionItem>
  );
};
